import React, { useState, useEffect } from 'react';
import {
   Card,
   CardContent,
   Typography,
   Avatar,
   Box,
   Chip,
   CircularProgress,
} from '@mui/material';
import {
   PhotoAlbum as PhotoAlbumIcon,
   Lock as LockIcon,
   Public as PublicIcon,
   CalendarToday as CalendarTodayIcon,
   Photo as PhotoIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

export interface AlbumCreated {
   id: string;
   card_type: 'album_created';
   userid: string;
   albumId: string;
   albumTitle: string;
   albumDescription?: string;
   albumCoverUrl?: string;
   albumPhotoCount: number;
   albumType: 'private' | 'public' | 'paid';
   albumIsPublic: boolean;
   albumDate?: string;
   albumPhotos?: string[];
   creatorId: string;
   creatorName: string;
   creatorPhoto?: string;
   creatorEmail: string;
   createdAt: any;
   seen: boolean;
}

interface AlbumCreatedCardProps {
   albumCreated: AlbumCreated;
   onViewAlbum: (albumId: string) => void;
   onMarkAsSeen?: (cardId: string) => void;
}

const AlbumCreatedCard: React.FC<AlbumCreatedCardProps> = ({
   albumCreated,
   onViewAlbum,
   onMarkAsSeen,
}) => {
   const [albumData, setAlbumData] = useState<any>(null);
   const [loading, setLoading] = useState(true);

   useEffect(() => {
      const fetchAlbumData = async () => {
         try {
            const albumDoc = await getDoc(doc(db, 'albums', albumCreated.albumId));
            if (albumDoc.exists()) {
               setAlbumData(albumDoc.data());
            }
         } catch (error) {
            console.error('Error fetching album data:', error);
         } finally {
            setLoading(false);
         }
      };

      fetchAlbumData();
   }, [albumCreated.albumId]);

   const handleClick = () => {
      if (onMarkAsSeen) {
         onMarkAsSeen(albumCreated.id);
      }
      onViewAlbum(albumCreated.albumId);
   };

   const getTimeAgo = () => {
      if (!albumCreated.createdAt) return '';
      try {
         const date =
            albumCreated.createdAt.toDate?.() || new Date(albumCreated.createdAt.seconds * 1000);
         return formatDistanceToNow(date, { addSuffix: true });
      } catch {
         return '';
      }
   };

   if (loading) {
      return (
         <Card
            sx={{
               mb: 1.5,
               border: '1px solid',
               borderColor: 'divider',
               borderRadius: 2,
               p: 2,
            }}
         >
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 56 }}>
               <CircularProgress size={24} sx={{ color: '#00C4A7' }} />
            </Box>
         </Card>
      );
   }

   if (!albumData) {
      return null; // Album was deleted
   }

   const coverUrl = albumData.coverPhotoUrl || albumData.coverPhoto || albumCreated.albumCoverUrl;
   const photoCount = albumData.photoIds?.length || albumData.photoCount || albumCreated.albumPhotoCount;
   const isPublic = albumData.isPublic ?? albumCreated.albumIsPublic;

   return (
      <Card
         sx={{
            mb: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderLeft: '3px solid #22C55E',
            borderRadius: 2,
            transition: 'all 0.2s',
            cursor: 'pointer',
            '&:hover': {
               boxShadow: 3,
               transform: 'translateY(-2px)',
               borderColor: '#00C4A7',
            },
         }}
         onClick={handleClick}
      >
         <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
               {/* Album Cover Photo */}
               {coverUrl ? (
                  <Box
                     component="img"
                     src={coverUrl}
                     alt={albumData.title || albumCreated.albumTitle}
                     sx={{
                        width: 56,
                        height: 56,
                        objectFit: 'cover',
                        borderRadius: 2,
                     }}
                  />
               ) : (
                  <Avatar
                     sx={{
                        width: 56,
                        height: 56,
                        bgcolor: '#00C4A7',
                        borderRadius: 2,
                     }}
                  >
                     <PhotoAlbumIcon sx={{ fontSize: 32, color: 'white' }} />
                  </Avatar>
               )}

                  {/* Main Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#00C4A7' }}>
                           New Album Created!
                        </Typography>
                        <Chip
                           icon={
                              isPublic ? (
                                 <PublicIcon sx={{ fontSize: 14 }} />
                              ) : (
                                 <LockIcon sx={{ fontSize: 14 }} />
                              )
                           }
                           label={isPublic ? 'Public' : 'Private'}
                           size="small"
                           sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              bgcolor: isPublic ? '#10B981' : '#6366f1',
                              color: 'white',
                              '& .MuiChip-icon': {
                                 color: 'white',
                              },
                           }}
                        />
                     </Box>

                     <Typography
                        variant="body1"
                        sx={{
                           fontWeight: 600,
                           mb: 0.5,
                           overflow: 'hidden',
                           textOverflow: 'ellipsis',
                           display: '-webkit-box',
                           WebkitLineClamp: 1,
                           WebkitBoxOrient: 'vertical',
                        }}
                     >
                        {albumData.title || albumCreated.albumTitle}
                     </Typography>

                     {(albumData.description || albumCreated.albumDescription) && (
                        <Typography
                           variant="body2"
                           color="text.secondary"
                           sx={{
                              mb: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                           }}
                        >
                           {albumData.description || albumCreated.albumDescription}
                        </Typography>
                     )}

                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           <PhotoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                           <Typography variant="caption" color="text.secondary">
                              {photoCount} photo{photoCount !== 1 ? 's' : ''}
                           </Typography>
                        </Box>

                        {(albumData.albumDate || albumCreated.albumDate) && (
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                 {new Date(albumData.albumDate || albumCreated.albumDate).toLocaleDateString()}
                              </Typography>
                           </Box>
                        )}

                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                           {getTimeAgo()}
                        </Typography>
                     </Box>
                  </Box>
               </Box>
            </CardContent>
         </Card>
   );
};

export default AlbumCreatedCard;

